import { useRef, useState, type FormEvent, type ReactNode } from 'react';
import { useMutation } from '@tanstack/react-query';

import {
  ReceptionistSettingsSchema,
  type AgentRuntime,
  type ReceptionistEdit,
  type ReceptionistSettings,
} from '../../../api/agentRuntimeSchemas';
import { pairWhatsApp } from '../../../api/endpoints/agentRuntime';
import { ErrorBanner } from '../../../components/ErrorBanner';
import { useVersionConflict } from '../../../lib/use-version-conflict';
import { isVersionConflict } from '../../../lib/version-conflict';
import { QrCode } from './QrCode';
import { errorText, useSaveReceptionist, type SetupStep } from './useReception';

const TIME = new Intl.DateTimeFormat('es-PA', { hour: 'numeric', minute: '2-digit' });

type StepProps = { n: number; title: string; done: boolean; open: boolean; onOpen: () => void; children: ReactNode };

function Step({ n, title, done, open, onOpen, children }: StepProps) {
  return (
    <li className="reception-step" data-done={done || undefined} data-open={open || undefined}>
      <span className="reception-step-n" aria-hidden="true">{done ? '✓' : n}</span>
      <div className="reception-step-body">
        <h3>
          <button type="button" className="reception-step-toggle" aria-expanded={open} onClick={onOpen}>
            {title}
            {done ? <span className="sr-only"> (listo)</span> : null}
          </button>
        </h3>
        {open ? children : null}
      </div>
    </li>
  );
}

function WhatsAppStep({ runtime }: { runtime: AgentRuntime | undefined }) {
  const pairing = useMutation({ mutationFn: pairWhatsApp });
  const linked = runtime?.status.whatsapp === 'linked';
  if (linked) return <p className="row-sub">Listo: Luna contesta desde el WhatsApp del negocio.</p>;
  return (
    <div className="reception-pair">
      <p>En el teléfono del negocio abre WhatsApp, entra a Dispositivos vinculados y escanea el código. Tu WhatsApp sigue funcionando igual.</p>
      {pairing.data ? (
        <div className="reception-qr-box">
          <QrCode value={pairing.data.qr} />
          <p className="row-sub">Si vence, pide otro (vale hasta las {TIME.format(new Date(pairing.data.expiresAt))}).</p>
        </div>
      ) : null}
      {pairing.error ? <ErrorBanner message={errorText(pairing.error, 'No pudimos pedir el código. Intenta de nuevo.')} /> : null}
      <button type="button" className="btn" disabled={pairing.isPending} onClick={() => pairing.mutate()}>
        {pairing.isPending ? 'Pidiendo el código…' : pairing.data ? 'Pedir otro código' : 'Mostrar el código QR'}
      </button>
    </div>
  );
}

/** What Luna's form holds, in the units the server compares. */
type SettingsDraft = { instructions: string; tone: string; canBook: boolean; handoffPhone: string | null };
const SETTINGS_DRAFT_KEYS = ['instructions', 'tone', 'canBook', 'handoffPhone'] as const;

function settingsDraft(settings: ReceptionistSettings): SettingsDraft {
  return {
    instructions: settings.instructions,
    tone: settings.tone,
    canBook: settings.canBook,
    handoffPhone: settings.handoffPhone,
  };
}

function parseSettings(current: unknown): ReceptionistSettings | null {
  const parsed = ReceptionistSettingsSchema.safeParse(current);
  return parsed.success ? parsed.data : null;
}

export function SettingsForm({ settings, onSaved }: { settings: ReceptionistSettings; onSaved?: () => void }) {
  const save = useSaveReceptionist();
  const [instructions, setInstructions] = useState(settings.instructions);
  const [tone, setTone] = useState(settings.tone);
  const [canBook, setCanBook] = useState(settings.canBook);
  const [handoffPhone, setHandoffPhone] = useState(settings.handoffPhone ?? '');
  // What the form started from and its version (0 until the settings are first saved): a newer copy arriving
  // in the background never moves them, so a save always names what this person saw.
  const start = useRef({ draft: settingsDraft(settings), version: settings.version });

  function startFrom(record: ReceptionistSettings) {
    start.current = { draft: settingsDraft(record), version: record.version };
  }

  function seedForm(draft: SettingsDraft) {
    setInstructions(draft.instructions);
    setTone(draft.tone);
    setCanBook(draft.canBook);
    setHandoffPhone(draft.handoffPhone ?? '');
  }

  /** What the form holds now, trimmed the way it is sent. */
  function formDraft(): SettingsDraft {
    return { instructions: instructions.trim(), tone: tone.trim(), canBook, handoffPhone: handoffPhone.trim() || null };
  }

  function send(draft: SettingsDraft, version: number | undefined) {
    save.mutate(
      { ...draft, ...(version !== undefined ? { expectedVersion: version } : {}) },
      {
        onSuccess: (result) => {
          startFrom(result.data);
          onSaved?.();
        },
        onError: (error, edit) => {
          conflict.handle(error, { base: start.current.draft, mine: editDraft(edit) });
        },
      },
    );
  }

  const conflict = useVersionConflict<ReceptionistSettings, SettingsDraft>({
    keys: SETTINGS_DRAFT_KEYS,
    labels: {
      instructions: 'Qué debe saber Luna',
      tone: 'Cómo habla',
      canBook: 'Puede reservar sola',
      handoffPhone: 'A quién pasa la conversación',
    },
    parse: parseSettings,
    toDraft: settingsDraft,
    rebase: ({ draft, record }) => {
      seedForm(draft);
      startFrom(record);
    },
    save: ({ draft, version }) => send(draft, version),
    current: formDraft,
    discard: (record) => {
      seedForm(settingsDraft(record));
      startFrom(record);
    },
  });

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    conflict.clear();
    send(formDraft(), start.current.version);
  }
  return (
    <form className="form-grid reception-form" onSubmit={handleSubmit}>
      <label className="field">
        <span>Qué debe saber Luna de tu negocio</span>
        <textarea rows={4} value={instructions} onChange={(event) => setInstructions(event.target.value)} placeholder="Ejemplo: los sábados solo atendemos con cita; el parqueo es gratis en el sótano." />
      </label>
      <label className="field">
        <span>Cómo habla</span>
        <input type="text" value={tone} onChange={(event) => setTone(event.target.value)} placeholder="Cercano y breve, de tú" />
      </label>
      <label className="field field-check">
        <input type="checkbox" checked={canBook} onChange={(event) => setCanBook(event.target.checked)} />
        <span>Puede reservar sola cuando hay espacio. Si lo apagas, ofrece horarios y tú confirmas.</span>
      </label>
      <label className="field">
        <span>A quién pasa la conversación cuando hace falta una persona</span>
        <input type="tel" inputMode="tel" value={handoffPhone} onChange={(event) => setHandoffPhone(event.target.value)} placeholder="6000-0000" />
      </label>
      {save.error && !isVersionConflict(save.error) ? <ErrorBanner message={errorText(save.error, 'No pudimos guardar. Intenta de nuevo.')} /> : null}
      {save.data && !save.data.synced ? <p className="notice">Guardado. Luna lo toma en cuanto se conecte.</p> : null}
      {/* Where the person pressed Guardar: the choice appears in place of the button. */}
      {conflict.notice}
      {conflict.asking ? null : (
        <div>
          <button type="submit" className="btn" disabled={save.isPending}>
            {save.isPending ? 'Guardando…' : 'Guardar'}
          </button>
        </div>
      )}
    </form>
  );
}

/** The draft a save sent, in the units the server compares. */
function editDraft(edit: ReceptionistEdit): SettingsDraft {
  return { instructions: edit.instructions, tone: edit.tone, canBook: edit.canBook, handoffPhone: edit.handoffPhone };
}

type SetupProps = {
  runtime: AgentRuntime | undefined;
  settings: ReceptionistSettings;
  progress: Record<SetupStep, boolean>;
};

/** Three steps, one open at a time: the first one not done yet, or whichever the owner opens. */
export function SetupSteps({ runtime, settings, progress }: SetupProps) {
  const order: SetupStep[] = ['whatsapp', 'settings', 'try'];
  const [picked, setPicked] = useState<SetupStep | null>(null);
  const current = picked ?? order.find((step) => !progress[step]) ?? 'try';
  const stepProps = (step: SetupStep) => ({ done: progress[step], open: current === step, onOpen: () => setPicked(step) });
  return (
    <ol className="reception-steps">
      <Step n={1} title="Conecta el WhatsApp del negocio" {...stepProps('whatsapp')}>
        <WhatsAppStep runtime={runtime} />
      </Step>
      <Step n={2} title="Cuéntale cómo atiendes" {...stepProps('settings')}>
        <SettingsForm settings={settings} />
      </Step>
      <Step n={3} title="Pruébala" {...stepProps('try')}>
        <p>Desde otro teléfono, escríbele al WhatsApp del negocio: “Hola, ¿tienen espacio mañana en la tarde?”. La conversación aparece en Bandeja.</p>
      </Step>
    </ol>
  );
}
